/**
 * Performance Optimization Engine
 *
 * Handles CPU-Memory speed optimization and VRM analysis
 */

import type {
  CompatibilityResult,
  CompatibilityChecker
} from '../types/CompatibilityResult'
import type { CPUModel } from '../../models/cpu/cpu-model'
import type { MemoryModel } from '../../models/memory/memory-model'
import type { MotherboardModel } from '../../models/motherboard/motherboard-model'

/**
 * CPU-Memory Speed Optimization Checker
 */
export class CPUMemorySpeedChecker implements CompatibilityChecker<CPUModel, MemoryModel> {
  check(cpu: CPUModel, memory: MemoryModel): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    // Memory speed optimization check
    const speedCheck = this.checkMemorySpeedOptimization(cpu, memory)
    results.push(speedCheck)

    // Memory type compatibility
    const typeCheck = this.checkMemoryTypeCompatibility(cpu, memory)
    results.push(typeCheck)

    return results
  }

  getScore(cpu: CPUModel, memory: MemoryModel): number {
    if (!cpu.memorySupport) return 0

    const cpuMaxSpeed = cpu.memorySupport.maxSpeed
    const memorySpeed = memory.speed ?? memory.specifications?.speed ?? 3200
    const jedecSpeed = memory.specifications?.jedecSpeed ?? (memory.type === 'DDR5' ? 4800 : 2133)

    // Perfect match with CPU capability
    if (memorySpeed <= cpuMaxSpeed && memorySpeed > jedecSpeed) return 100

    // JEDEC standard speed
    if (memorySpeed === jedecSpeed) return 90

    // Overclocked but within 10% tolerance
    if (memorySpeed > cpuMaxSpeed && memorySpeed <= cpuMaxSpeed * 1.1) return 85

    // Significantly overclocked
    if (memorySpeed > cpuMaxSpeed) return 70

    return 60
  }

  private checkMemorySpeedOptimization(cpu: CPUModel, memory: MemoryModel): CompatibilityResult {
    if (!cpu.memorySupport) {
      return {
        level: 'info',
        category: 'performance',
        message: 'メモリ仕様情報が不足しています',
        score: 0
      }
    }

    const cpuMaxSpeed = cpu.memorySupport.maxSpeed
    const memorySpeed = memory.speed ?? memory.specifications?.speed ?? 3200

    if (memorySpeed > cpuMaxSpeed) {
      const actualSpeed = Math.min(memorySpeed, cpuMaxSpeed)
      return {
        level: 'warning',
        category: 'performance',
        message: `メモリ速度がCPUの公式サポート速度を超えています`,
        details: `メモリ: ${memorySpeed}MHz, CPU対応: ${cpuMaxSpeed}MHz → 実際動作: ${actualSpeed}MHz`,
        solutions: [
          'メモリオーバークロック設定(XMP/EXPO)を有効にする',
          `${cpuMaxSpeed}MHz以下のメモリを検討する`,
          'BIOS設定でメモリ速度を手動調整する'
        ],
        score: this.getScore(cpu, memory)
      }
    }

    if (memorySpeed < cpuMaxSpeed * 0.8) {
      return {
        level: 'info',
        category: 'performance',
        message: `より高速なメモリを使用することで性能向上が期待できます`,
        details: `現在: ${memorySpeed}MHz, CPU対応: ${cpuMaxSpeed}MHz`,
        solutions: [
          `${cpuMaxSpeed}MHzのメモリへのアップグレードを検討`,
          'デュアルチャネル構成の確認'
        ],
        score: this.getScore(cpu, memory)
      }
    }

    return {
      level: 'info',
      category: 'performance',
      message: `メモリ速度はCPUに最適化されています`,
      details: `メモリ: ${memorySpeed}MHz, CPU対応: ${cpuMaxSpeed}MHz`,
      score: this.getScore(cpu, memory)
    }
  }

  private checkMemoryTypeCompatibility(cpu: CPUModel, memory: MemoryModel): CompatibilityResult {
    if (!cpu.memorySupport) {
      return {
        level: 'info',
        category: 'memory',
        message: 'CPUメモリ仕様情報が不足しています',
        score: 0
      }
    }

    const cpuMemoryType = cpu.memorySupport.type
    const memoryType = memory.type

    if (!cpuMemoryType.includes(memoryType)) {
      return {
        level: 'critical',
        category: 'memory',
        message: `CPUとメモリのDDR規格が一致しません`,
        details: `CPU: ${cpuMemoryType}, メモリ: ${memoryType}`,
        solutions: [
          `${cpuMemoryType}対応メモリを選択する`,
          `${memoryType}対応CPUを選択する`
        ],
        score: 0
      }
    }

    return {
      level: 'info',
      category: 'memory',
      message: `メモリ規格は完全に互換性があります`,
      details: `${cpuMemoryType}規格で統一`,
      score: 100
    }
  }
}

/**
 * VRM Phase Analysis Checker
 */
export class VRMAnalysisChecker implements CompatibilityChecker<CPUModel, MotherboardModel> {
  check(cpu: CPUModel, motherboard: MotherboardModel): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    const vrmCheck = this.analyzeVRMAdequacy(cpu, motherboard)
    results.push(vrmCheck)

    return results
  }

  getScore(cpu: CPUModel, motherboard: MotherboardModel): number {
    const analysis = this.calculateVRMAnalysis(cpu, motherboard)

    switch (analysis.adequacy) {
      case 'excellent': return 100
      case 'good': return 85
      case 'adequate': return 70
      case 'insufficient': return 40
      default: return 60
    }
  }

  private analyzeVRMAdequacy(cpu: CPUModel, motherboard: MotherboardModel): CompatibilityResult {
    const analysis = this.calculateVRMAnalysis(cpu, motherboard)

    if (analysis.adequacy === 'insufficient') {
      return {
        level: 'warning',
        category: 'performance',
        message: `マザーボードのVRMフェーズ数がCPUに対して不足している可能性があります`,
        details: `CPU TDP: ${cpu.tdp}W, VRMフェーズ: ${analysis.vrmPhases}`,
        solutions: [
          '高負荷時のCPU温度を監視する',
          'より多くのVRMフェーズを持つマザーボードを検討する',
          'CPU電圧設定を見直す'
        ],
        score: this.getScore(cpu, motherboard)
      }
    }

    if (analysis.adequacy === 'adequate') {
      return {
        level: 'info',
        category: 'performance',
        message: `VRMフェーズ数は最低限の要件を満たしています`,
        details: `VRMフェーズ: ${analysis.vrmPhases}, オーバークロック適性: ${analysis.overclockingSuitability ? '良好' : '限定的'}`,
        solutions: analysis.overclockingSuitability ? [] : [
          'オーバークロック時は慎重に電圧・温度を監視する'
        ],
        score: this.getScore(cpu, motherboard)
      }
    }

    return {
      level: 'info',
      category: 'performance',
      message: `VRMフェーズ数はCPUに対して${analysis.adequacy === 'excellent' ? '非常に' : ''}十分です`,
      details: `VRMフェーズ: ${analysis.vrmPhases}, オーバークロック適性: 優秀`,
      score: this.getScore(cpu, motherboard)
    }
  }

  private calculateVRMAnalysis(cpu: CPUModel, motherboard: MotherboardModel): {
    adequacy: 'excellent' | 'good' | 'adequate' | 'insufficient'
    vrmPhases: number
    overclockingSuitability: boolean
  } {
    const vrmPhasesStr = motherboard.vrm?.phaseCount ? `${motherboard.vrm.phaseCount}` : '8+2'
    const vrmPhases = this.parseVRMPhases(vrmPhasesStr)
    const cpuTdp = cpu.tdp

    let adequacy: 'excellent' | 'good' | 'adequate' | 'insufficient'
    let overclockingSuitability = false

    if (vrmPhases.total >= 16 && cpuTdp <= 65) {
      adequacy = 'excellent'
      overclockingSuitability = true
    } else if (vrmPhases.total >= 12 && cpuTdp <= 105) {
      adequacy = 'good'
      overclockingSuitability = cpuTdp <= 95
    } else if (vrmPhases.total >= 8 && cpuTdp <= 125) {
      adequacy = 'adequate'
      overclockingSuitability = cpuTdp <= 95 && vrmPhases.total >= 10
    } else {
      adequacy = 'insufficient'
      overclockingSuitability = false
    }

    return {
      adequacy,
      vrmPhases: vrmPhases.total,
      overclockingSuitability
    }
  }

  private parseVRMPhases(vrmString: string): { total: number; cpu: number; soc: number } {
    // Parse "18+2" or "24+1+1" format
    const parts = vrmString.split('+').map(n => parseInt(n.trim()) || 0)
    return {
      total: parts.reduce((sum, n) => sum + n, 0),
      cpu: parts[0] || 0,
      soc: parts[1] || 0
    }
  }
}

/**
 * Performance Optimization Engine
 * Coordinates all performance optimization checks
 */
export class PerformanceOptimizationEngine {
  private cpuMemoryChecker = new CPUMemorySpeedChecker()
  private vrmAnalyzer = new VRMAnalysisChecker()

  checkCPUMemoryOptimization(cpu: CPUModel, memory: MemoryModel): CompatibilityResult[] {
    return this.cpuMemoryChecker.check(cpu, memory)
  }

  checkVRMAdequacy(cpu: CPUModel, motherboard: MotherboardModel): CompatibilityResult[] {
    return this.vrmAnalyzer.check(cpu, motherboard)
  }

  getOverallPerformanceScore(components: {
    cpu?: CPUModel
    memory?: MemoryModel
    motherboard?: MotherboardModel
  }): number {
    const scores: number[] = []

    if (components.cpu && components.memory) {
      scores.push(this.cpuMemoryChecker.getScore(components.cpu, components.memory))
    }

    if (components.cpu && components.motherboard) {
      scores.push(this.vrmAnalyzer.getScore(components.cpu, components.motherboard))
    }

    return scores.length > 0 ? scores.reduce((a, b) => a + b) / scores.length : 100
  }

  generatePerformanceRecommendations(components: {
    cpu?: CPUModel
    memory?: MemoryModel
    motherboard?: MotherboardModel
  }): string[] {
    const recommendations: string[] = []

    if (components.cpu && components.memory) {
      const memoryScore = this.cpuMemoryChecker.getScore(components.cpu, components.memory)
      if (memoryScore < 90) {
        recommendations.push('メモリ速度とCPUの相性を最適化することで性能向上が期待できます')
      }
    }

    if (components.cpu && components.motherboard) {
      const vrmScore = this.vrmAnalyzer.getScore(components.cpu, components.motherboard)
      if (vrmScore < 80) {
        recommendations.push('より多くのVRMフェーズを持つマザーボードでCPU性能を最大化できます')
      }
    }

    return recommendations
  }
}